import { useState, useRef, useEffect } from 'react'
import { X, Send, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatStore } from '@/stores/chatStore'
import {
  useVisitorMessages,
  useCreateSession,
  useSendVisitorMessage,
  useChatRealtime,
} from '@/hooks/useChat'
import { ChatMessage } from './ChatMessage'
import type { ChatMessage as ChatMessageType } from '@/types'

export function ChatPanel() {
  const {
    isOnline,
    visitorId,
    sessionId,
    visitorName,
    visitorEmail,
    setOpen,
    setSession,
    setVisitorInfo,
    setMessages,
    messages: storeMessages,
  } = useChatStore()

  const [nameInput, setNameInput] = useState('')
  const [emailInput, setEmailInput] = useState('')
  const [messageInput, setMessageInput] = useState('')
  const [offlineMessage, setOfflineMessage] = useState('')
  const [offlineSubmitted, setOfflineSubmitted] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Load messages from server
  const { data: serverMessages } = useVisitorMessages(sessionId)
  const createSession = useCreateSession()
  const sendMessage = useSendVisitorMessage()

  // Setup realtime
  useChatRealtime(sessionId, 'visitor')

  // Sync server messages to store
  useEffect(() => {
    if (serverMessages && serverMessages.length > 0) {
      setMessages(serverMessages)
    }
  }, [serverMessages, setMessages])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [storeMessages])

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!nameInput.trim()) return
    setVisitorInfo(nameInput.trim(), emailInput.trim() || undefined)
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!messageInput.trim()) return

    const content = messageInput.trim()
    setMessageInput('')

    let currentSessionId = sessionId

    // Create session on first message
    if (!currentSessionId) {
      try {
        const session = await createSession.mutateAsync({
          visitor_id: visitorId,
          visitor_name: visitorName,
          visitor_email: visitorEmail,
        })
        currentSessionId = session.id
        setSession(session.id)
      } catch {
        return
      }
    }

    // Send message
    try {
      const msg = await sendMessage.mutateAsync({
        session_id: currentSessionId,
        content,
      })
      // Optimistically append to store
      useChatStore.getState().appendMessage(msg)
    } catch {
      // Restore input on failure
      setMessageInput(content)
    }
  }

  const handleOfflineSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nameInput.trim() || !emailInput.trim() || !offlineMessage.trim()) return

    try {
      const session = await createSession.mutateAsync({
        visitor_id: visitorId,
        visitor_name: nameInput.trim(),
        visitor_email: emailInput.trim(),
      })

      await sendMessage.mutateAsync({
        session_id: session.id,
        content: offlineMessage.trim(),
      })

      setVisitorInfo(nameInput.trim(), emailInput.trim())
      setSession(session.id)
      setOfflineSubmitted(true)
    } catch {
      // Error handled by mutation
    }
  }

  // Determine render mode
  const hasName = !!visitorName
  const showNameForm = isOnline && !hasName
  const showChat = isOnline && hasName
  const showOffline = !isOnline

  return (
    <div className="w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-secondary-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary-600 text-white">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">Engitronix Chat</span>
          <span
            className={cn(
              'w-2 h-2 rounded-full',
              isOnline ? 'bg-green-400' : 'bg-secondary-400'
            )}
          />
        </div>
        <button
          onClick={() => setOpen(false)}
          className="p-1 hover:bg-primary-700 rounded transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {showNameForm && <NameForm
          nameInput={nameInput}
          emailInput={emailInput}
          onNameChange={setNameInput}
          onEmailChange={setEmailInput}
          onSubmit={handleNameSubmit}
        />}

        {showChat && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {storeMessages.length === 0 && (
                <div className="text-center text-secondary-400 text-sm py-8">
                  <p>Hello {visitorName}!</p>
                  <p className="mt-1">How can we help you today?</p>
                </div>
              )}
              {storeMessages.map((msg: ChatMessageType) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message input */}
            <form onSubmit={handleSendMessage} className="p-3 border-t border-secondary-100">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!messageInput.trim() || sendMessage.isPending}
                  className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sendMessage.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        {showOffline && !offlineSubmitted && (
          <OfflineForm
            nameInput={nameInput}
            emailInput={emailInput}
            messageInput={offlineMessage}
            onNameChange={setNameInput}
            onEmailChange={setEmailInput}
            onMessageChange={setOfflineMessage}
            onSubmit={handleOfflineSubmit}
            isPending={createSession.isPending || sendMessage.isPending}
          />
        )}

        {showOffline && offlineSubmitted && (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-medium text-secondary-900">Message sent!</p>
              <p className="text-sm text-secondary-500 mt-1">
                We'll get back to you via email as soon as possible.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────

function NameForm({
  nameInput,
  emailInput,
  onNameChange,
  onEmailChange,
  onSubmit,
}: {
  nameInput: string
  emailInput: string
  onNameChange: (v: string) => void
  onEmailChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <form onSubmit={onSubmit} className="w-full space-y-4">
        <div className="text-center mb-4">
          <p className="font-medium text-secondary-900">Welcome!</p>
          <p className="text-sm text-secondary-500 mt-1">
            Please introduce yourself to start chatting.
          </p>
        </div>
        <div>
          <input
            type="text"
            value={nameInput}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Your name *"
            required
            className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            autoFocus
          />
        </div>
        <div>
          <input
            type="email"
            value={emailInput}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="Email (optional)"
            className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          disabled={!nameInput.trim()}
          className="w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Start Chat
        </button>
      </form>
    </div>
  )
}

function OfflineForm({
  nameInput,
  emailInput,
  messageInput,
  onNameChange,
  onEmailChange,
  onMessageChange,
  onSubmit,
  isPending,
}: {
  nameInput: string
  emailInput: string
  messageInput: string
  onNameChange: (v: string) => void
  onEmailChange: (v: string) => void
  onMessageChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  isPending: boolean
}) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="text-center mb-2">
          <p className="font-medium text-secondary-900">We're offline</p>
          <p className="text-sm text-secondary-500 mt-1">
            Leave a message and we'll get back to you.
          </p>
        </div>
        <input
          type="text"
          value={nameInput}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Your name *"
          required
          className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <input
          type="email"
          value={emailInput}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="Your email *"
          required
          className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <textarea
          value={messageInput}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder="Your message *"
          required
          rows={4}
          className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
        />
        <button
          type="submit"
          disabled={!nameInput.trim() || !emailInput.trim() || !messageInput.trim() || isPending}
          className="w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Send Message
        </button>
      </form>
    </div>
  )
}
